/*
 * Copyright (c) 2026 Dobler Data Solutions, LLC
 * Fabric Control Plane - Item Lineage Graph
 * Feature #183: External Metadata Enrichment
 * Constitution: v1.18.0
 */

import React, { useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  NodeTypes,
  useNodesState,
  useEdgesState,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { GitBranch, Database } from 'lucide-react';
import { useItemLineage } from '../../services/operations/metadataService';
import { PageLayout, LoadingState, ErrorState, EmptyState } from '../../components/ui';
import type { ItemLineageLink } from '@shared/types/metadata';

const SOURCE_TYPE_COLORS: Record<string, string> = {
  FabricItem: '#3b82f6',
  ExternalDatasource: '#f59e0b',
  Unknown: '#6b7280',
};

const FabricItemNode: React.FC<{ data: { label: string; itemType: string; color: string } }> = ({ data }) => (
  <div
    className="rounded-lg border-2 px-3 py-2 text-sm shadow"
    style={{ borderColor: data.color, background: `${data.color}18` }}
  >
    <Handle type="target" position={Position.Left} />
    <div className="font-semibold text-[var(--color-text-primary)]">{data.label}</div>
    {data.itemType && <div className="text-xs text-[var(--color-text-secondary)]">{data.itemType}</div>}
    <Handle type="source" position={Position.Right} />
  </div>
);

const nodeTypes: NodeTypes = { fabricItem: FabricItemNode };

export interface LineageGraphPageProps {
  /** When set, the page renders as modal body content (no PageLayout chrome). */
  embeddedId?: string;
}

export const LineageGraphPage: React.FC<LineageGraphPageProps> = ({ embeddedId }) => {
  const params = useParams<{ id: string }>();
  const id = embeddedId ?? params.id;
  const embedded = !!embeddedId;
  const { data, isLoading, isError, refetch } = useItemLineage(id ?? null);

  const { initialNodes, initialEdges } = useMemo(() => {
    if (!data) return { initialNodes: [], initialEdges: [] };

    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Root node (the item we're viewing)
    nodes.push({
      id: data.itemId,
      type: 'fabricItem',
      position: { x: 400, y: 200 },
      data: { label: data.itemName ?? data.itemId, itemType: data.itemType ?? '', color: '#3b82f6' },
    });

    (data.upstreamLinks ?? []).forEach((link: ItemLineageLink, idx: number) => {
      const sourceId = link.sourceItemId ?? `ext-${idx}`;
      const color = SOURCE_TYPE_COLORS[link.sourceType] ?? SOURCE_TYPE_COLORS.Unknown;

      if (!nodes.find((n) => n.id === sourceId)) {
        nodes.push({
          id: sourceId,
          type: 'fabricItem',
          position: { x: 100, y: 80 + idx * 120 },
          data: {
            label: link.sourceItemId ? (link.sourceItemName ?? link.sourceItemId) : (link.datasourceType ?? 'External Source'),
            itemType: link.sourceType,
            color,
          },
        });
      }

      edges.push({
        id: `edge-${idx}`,
        source: sourceId,
        target: data.itemId,
        label: link.linkType,
        style: { stroke: color },
        animated: link.sourceType === 'ExternalDatasource',
      });
    });

    return { initialNodes: nodes, initialEdges: edges };
  }, [data]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // useNodesState/useEdgesState only seed from their initial value on mount. The
  // lineage data arrives asynchronously, so re-sync whenever the derived graph
  // changes — otherwise the canvas is empty until a manual refresh.
  useEffect(() => {
    setNodes(initialNodes);
    setEdges(initialEdges);
  }, [initialNodes, initialEdges, setNodes, setEdges]);

  const onInit = useCallback(() => {}, []);

  // Wrap content in PageLayout for the full-page route; render bare for the modal.
  const frame = (title: string, body: React.ReactNode, description?: string) =>
    embedded ? (
      <div className="p-4">{body}</div>
    ) : (
      <PageLayout
      helpId="auto.lineage-graph" title={title} description={description} icon={<GitBranch className="h-6 w-6" />}>
        {body}
      </PageLayout>
    );

  if (isLoading) return frame('Item Lineage', <LoadingState rows={6} />);
  if (isError) return frame('Item Lineage', <ErrorState title="Failed to load lineage data" onRetry={() => void refetch()} />);
  if (!data) return frame('Item Lineage', <EmptyState icon={<Database className="h-10 w-10" />} title="Item not found" message="This Fabric item does not exist or has not been collected." />);

  if ((data.upstreamLinks ?? []).length === 0) {
    return frame(
      data.itemName ?? data.itemId,
      <EmptyState
        icon={<GitBranch className="h-10 w-10" />}
        title="No lineage collected"
        message="This item has no upstream lineage links. Lineage is collected via Scanner API during the daily tier run."
      />,
    );
  }

  const linkCount = data.upstreamLinks.length;
  const graph = (
    <div className="h-[520px] overflow-hidden rounded-lg border border-[var(--color-border)]">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onInit={onInit}
        nodeTypes={nodeTypes}
        fitView
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );

  if (embedded) {
    return (
      <div className="space-y-3 p-4">
        <div>
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{data.itemName ?? data.itemId}</h2>
          <p className="text-sm text-[var(--color-text-secondary)]">{linkCount} upstream link{linkCount === 1 ? '' : 's'}</p>
        </div>
        {graph}
      </div>
    );
  }

  return (
    <PageLayout
      title={data.itemName ?? data.itemId}
      description={`${linkCount} upstream link${linkCount === 1 ? '' : 's'}`}
      icon={<GitBranch className="h-6 w-6" />}
    >
      {graph}
    </PageLayout>
  );
};

export default LineageGraphPage;
